import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, vModelText as _vModelText, withKeys as _withKeys, withDirectives as _withDirectives, vModelCheckbox as _vModelCheckbox, resolveComponent as _resolveComponent, createVNode as _createVNode, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "grid w-full grid-cols-1 sm:grid-cols-2" }
const _hoisted_2 = { class: "flex w-full flex-col items-center bg-gray-50 px-8 pt-20 sm:items-start sm:justify-center sm:pt-0 lg:px-28 xl:px-40" }
const _hoisted_3 = { class: "mb-4 text-center text-2xl font-semibold text-gray-700" }
const _hoisted_4 = { class: "mb-6 max-w-xl text-center text-sm" }
const _hoisted_5 = {
  key: 0,
  class: "mb-4 flex w-full justify-center"
}
const _hoisted_6 = ["src"]
const _hoisted_7 = { class: "flex w-full max-w-sm flex-col items-center gap-3" }
const _hoisted_8 = { class: "flex items-center gap-2 self-start text-sm text-gray-600" }
const _hoisted_9 = ["disabled"]

import { ref, onMounted, computed } from 'vue';
import QRCode from 'qrcode';
import { useI18n } from 'vue-i18n';
import AuthService from '@/services/api/AuthService';
import router from '@/router';
import { useUiStore, useAuthStore } from '@/stores';
import { toastServiceError } from '@/services';


export default /*@__PURE__*/_defineComponent({
  __name: 'TwoFaAuthenticatorPage',
  setup(__props) {

const { t } = useI18n();

const qrCodeUrl = ref('');

const formState = ref({
    code: '',
    remember: false,
});

const uiStore = useUiStore();
const authStore = useAuthStore();

const setLoading = (loading: boolean) => uiStore.setIsLoading(loading);

const twoFactorEnabled = computed(() => authStore.user && authStore.user.twoFactorEnabled);

const twoFaSetup = async () => {
    try {
        const response = await AuthService.twoFaSetup();
        qrCodeUrl.value = await QRCode.toDataURL(response.data.otpAuthUrl);
    } catch (err) {
        await router.push('/login');
        console.error(err);
    }
};

const twoFaVerifySetup = async () => {
    if (!formState.value.code) {
        toastServiceError(t('authentication.2fa.error.enterCode'));
        return;
    }
    try {
        setLoading(true);
        const { data } = await AuthService.twoFaVerifySetup({
            code: formState.value.code,
            remember: formState.value.remember,
        });
        authStore.login(data);

        await router.push('/');
    } catch (error) {
        console.log(error);
        toastServiceError(error);
    } finally {
        setLoading(false);
    }
};

const twoFaVerify = async () => {
    if (!formState.value.code) {
        toastServiceError(t('authentication.2fa.error.enterCode'));
        return;
    }
    try {
        setLoading(true);
        const { data } = await AuthService.twoFaVerify({
            code: formState.value.code,
            remember: formState.value.remember,
        });

        authStore.login(data);
        await router.push('/');
    } catch (error) {
        console.log(error);
        toastServiceError(error);
    } finally {
        setLoading(false);
    }
};

const verify = async () => {
    if (twoFactorEnabled.value) {
        await twoFaVerify();
    } else {
        await twoFaVerifySetup();
    }
};

onMounted(async () => {
    if (!twoFactorEnabled.value) {
        await twoFaSetup();
    }
});

return (_ctx: any,_cache: any) => {
  const _component_UiLoadingDialog = _resolveComponent("UiLoadingDialog")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _cache[3] || (_cache[3] = _createElementVNode("div", { class: "bg-primary relative hidden w-full items-center justify-center sm:flex" }, [
        _createElementVNode("span", { class: "login-bg h-[150px] xl:h-[250px]" })
      ], -1)),
      _createElementVNode("div", _hoisted_2, [
        _cache[2] || (_cache[2] = _createElementVNode("span", { class: "login-logo mb-8 w-[225px]" }, null, -1)),
        _createElementVNode("h3", _hoisted_3, _toDisplayString(_unref(t)('authentication.2fa.title')), 1),
        _createElementVNode("p", _hoisted_4, _toDisplayString(_unref(t)('authentication.2fa.description')), 1),
        (qrCodeUrl.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
              _createElementVNode("img", {
                src: qrCodeUrl.value,
                alt: "2FA QR Code",
                class: "h-48 w-48"
              }, null, 8, _hoisted_6)
            ]))
          : _createCommentVNode("", true),
        _createElementVNode("div", _hoisted_7, [
          _withDirectives(_createElementVNode("input", {
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((formState.value.code) = $event)),
            type: "text",
            inputmode: "numeric",
            maxlength: "6",
            placeholder: "123456",
            onKeydown: _withKeys(verify, ["enter"]),
            class: "w-full rounded border border-gray-300 px-3 py-2 text-center text-lg tracking-widest focus:outline-none focus:ring-2 focus:ring-blue-500"
          }, null, 544), [
            [_vModelText, formState.value.code]
          ]),
          _createElementVNode("label", _hoisted_8, [
            _withDirectives(_createElementVNode("input", {
              type: "checkbox",
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((formState.value.remember) = $event))
            }, null, 512), [
              [_vModelCheckbox, formState.value.remember]
            ]),
            _createElementVNode("span", null, _toDisplayString(_unref(t)('authentication.2fa.form.rememberMe')), 1)
          ]),
          _createElementVNode("button", {
            class: "w-full rounded bg-blue-600 px-4 py-2 font-semibold text-white hover:bg-blue-700 disabled:opacity-50",
            disabled: !formState.value.code,
            onClick: verify
          }, _toDisplayString(_unref(t)('authentication.2fa.form.verify')), 9, _hoisted_9)
        ])
      ])
    ]),
    _createVNode(_component_UiLoadingDialog, {
      show: _unref(uiStore).isLoading,
      text: _ctx.$t('ui-loading-dialog-text')
    }, null, 8, ["show", "text"])
  ], 64))
}
}

})